import { TypeValue } from '@ha/algolia';
import {
  ListingPricingType,
  ListingRentalPeriodType,
} from '@ha/api-v2/types/Listing';
import type { ListingPricingValues } from '@ha/api-v2/types/Listing';

import { getPrecisePriceRange } from '../PaymentPricing/listing/getPrecisePriceRange';

export type ListingCardPriceInformation =
  | { type: 'value'; value: number; isMinimum: boolean }
  | { type: 'range'; range: { minPrice: number; maxPrice: number } };

export type ListingCardPriceListing = {
  recordType?: string | null;
  propertyMinPriceOverall?: number;
  propertyMinPriceSharedRooms?: number;
  propertyMinPricePrivateRooms?: number;
  propertyMinPriceStudios?: number;
  propertyMinPriceApartments?: number;
  rentalPeriodType?: ListingRentalPeriodType | string | null;
  minPrice?: number;
  maxPrice?: number;
  price?: number;
  /** Algolia-normalized listings type this as `string`. */
  priceType?: ListingPricingType | 'flat' | 'monthly' | string | null;
  /**
   * Accept Algolia-normalized `Record<string, number>` monthly maps as well as
   * stricter `ListingPricingValues` from the API.
   */
  pricingValues?: {
    flat: number;
    monthly: Record<string, number> | null;
    precise?: Record<string, number>;
  } | null;
};

export type ListingCardPriceDateRange = {
  from: string | Date;
  to?: string | Date;
};

const priceInformationValue = (
  value: number,
  { isMinimum = false }: { isMinimum?: boolean } = {},
): ListingCardPriceInformation => ({
  type: 'value',
  value,
  isMinimum,
});

const priceInformationRange = (
  minPrice: number,
  maxPrice: number,
): ListingCardPriceInformation => {
  if (minPrice === maxPrice) {
    return priceInformationValue(minPrice);
  }

  return { type: 'range', range: { minPrice, maxPrice } };
};

const resolvePropertyMinPrice = (
  listing: ListingCardPriceListing,
  categories?: TypeValue[],
): number | null => {
  if (listing.propertyMinPriceOverall === undefined) {
    return null;
  }

  if (!categories?.length) {
    return listing.propertyMinPriceOverall;
  }

  const prices: Record<TypeValue, number | undefined> = {
    [TypeValue.SHARED_ROOM]: listing.propertyMinPriceSharedRooms,
    [TypeValue.PRIVATE_ROOM]: listing.propertyMinPricePrivateRooms,
    [TypeValue.STUDIO]: listing.propertyMinPriceStudios,
    [TypeValue.APARTMENT]: listing.propertyMinPriceApartments,
    [TypeValue.STUDENT_HOUSING]: undefined,
  };

  const values = categories
    .map(category => prices[category])
    // 0 is the "no units of this type" sentinel, not a real price
    .filter((value): value is number => value != null && value > 0);

  return values.length ? Math.min(...values) : listing.propertyMinPriceOverall;
};

/**
 * Pure listing-card price resolution (search card parity).
 * Pass already currency-normalized Algolia fields.
 */
export const getListingCardPriceInformation = ({
  listing,
  dateRange,
  categories,
}: {
  listing: ListingCardPriceListing | null;
  dateRange?: ListingCardPriceDateRange;
  categories?: TypeValue[];
}): ListingCardPriceInformation | null => {
  if (!listing) {
    return null;
  }

  if (listing.recordType === 'property') {
    const value = resolvePropertyMinPrice(listing, categories);
    if (value == null) {
      return null;
    }

    return priceInformationValue(value, { isMinimum: true });
  }

  if (listing.rentalPeriodType === ListingRentalPeriodType.STRICT) {
    if (listing.minPrice === undefined) {
      return null;
    }

    if (listing.minPrice === 0) {
      return priceInformationValue(listing.price ?? 0);
    }

    if (listing.maxPrice === undefined) {
      return priceInformationValue(listing.minPrice);
    }

    return priceInformationRange(listing.minPrice, listing.maxPrice);
  }

  if (
    listing.pricingValues?.precise &&
    Object.keys(listing.pricingValues.precise).length > 0
  ) {
    if (listing.priceType == null || listing.pricingValues.precise == null) {
      return null;
    }

    if (!dateRange?.from) {
      return null;
    }

    const precisePriceRange = getPrecisePriceRange({
      pricingType: listing.priceType as ListingPricingType,
      pricingValues: listing.pricingValues as ListingPricingValues,
      dateRange,
    });

    if (!precisePriceRange?.from) {
      return null;
    }

    return priceInformationRange(precisePriceRange.from, precisePriceRange.to);
  }

  if (listing.priceType === 'monthly' && listing.minPrice) {
    if (listing.maxPrice === undefined) {
      return priceInformationValue(listing.minPrice);
    }

    return priceInformationRange(listing.minPrice, listing.maxPrice);
  }

  if (listing.price === undefined) {
    return null;
  }

  return priceInformationValue(listing.price / 100);
};
